'use client';

import * as React from 'react';
import { XIcon } from 'lucide-react';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverFacetsQuery } from '@loci/api';

import {
  SelectMenu,
  SelectMenuContent,
  SelectMenuItem,
  SelectMenuTrigger,
  SelectMenuValue,
} from '@/components/atoms/Select';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/components/atoms/primitives/sheet';
import { cn } from '@/components/atoms/primitives/utils';
import { DiscoverActiveFilters } from '@/components/molecules/DiscoverActiveFilters';
import { DiscoverCategoryFilter } from '@/components/molecules/DiscoverCategoryFilter';
import { DiscoverDatePills } from '@/components/molecules/DiscoverDatePills';
import { DiscoverFollowToggle } from '@/components/molecules/DiscoverFollowToggle';
import { DiscoverNeighborhoodFilter } from '@/components/molecules/DiscoverNeighborhoodFilter';
import { DiscoverPricePresets } from '@/components/molecules/DiscoverPricePresets';
import { DiscoverScopeCaptionView } from '@/components/molecules/DiscoverScopeCaption';
import { CATEGORY_ORDER } from '@/config/categories';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import { countActiveFilters } from '@/lib/discover/activeFilterCount';
import {
  formatDiscoverScopeCaptionPhrase,
  resolveDiscoverScopeKey,
} from '@/lib/discover/resultsCountCopy';
import { buildSortOptions } from '@/lib/discover/sortOptions';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';
import { defaultDiscoverParams, type SortMode } from '@/lib/discover/url-state';
import {
  DISCOVER_RESULTS_REGION_TEST_ID,
  MOBILE_FILTER_SHEET_BREAKPOINT_PX,
  MOBILE_FILTER_SHEET_DRAG_DISMISS_PX,
  MOBILE_FILTER_SHEET_DRAG_Y_VAR,
  MOBILE_FILTER_SHEET_PANEL_ID,
} from './mobileFilterSheet.constants';
import { DiscoverRailHeadCards } from './DiscoverRailHeadCards';

const SHEET_OVERLAY_CLASS = cn(
  'discover-filter-sheet-overlay z-modal',
  'data-[state=open]:duration-300 data-[state=closed]:duration-300',
);

const SHEET_CONTENT_CLASS = cn(
  'discover-filter-sheet-surface z-modal',
  'inset-x-0 bottom-0 top-auto h-auto w-full max-w-none sm:max-w-none',
  'max-h-[min(88dvh,720px)]',
  'rounded-t-[20px] rounded-b-none border-0 p-0 gap-0',
  'bg-background text-foreground',
  // Spec: translateY(101%) → 0 over 300ms (the extra 1% hides the shadow).
  // Open reads `--sheet-drag-y` (MOBILE_FILTER_SHEET_DRAG_Y_VAR) so the
  // `!important` pin cannot override the live grabber offset. Keep this
  // class a static literal — Tailwind will not emit an interpolated utility.
  'data-[state=open]:duration-300 data-[state=closed]:duration-300',
  'data-[state=closed]:!translate-y-[101%] data-[state=open]:!translate-y-[var(--sheet-drag-y,0px)]',
  'flex flex-col overflow-hidden',
);

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatApplyLabel(total: number | undefined): string {
  if (total === undefined) return 'Apply';
  return `Show ${total} ${total === 1 ? 'event' : 'events'}`;
}

/**
 * `offsetParent` is null for `display: none` *and* for `position: fixed`,
 * so a layout-box check would false-negative sticky/fixed chrome. Walk
 * computed style instead — `min-[981px]:hidden` is `display: none`.
 */
function isVisibleForFocus(element: HTMLElement): boolean {
  if (!element.isConnected) return false;
  let current: HTMLElement | null = element;
  while (current) {
    const { display, visibility } = getComputedStyle(current);
    if (display === 'none' || visibility === 'hidden') return false;
    current = current.parentElement;
  }
  return true;
}

const CLOSE_FOCUS_FALLBACK_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Prefer the FILTERS trigger. When the sheet auto-closes past 981px the
 * trigger stays mounted but CSS-hidden, and `.focus()` on it is a no-op
 * that drops the user on `<body>`. Land on the nearest visible focusable
 * in an ancestor (the results-bar view tabs) instead.
 */
function resolveCloseFocusTarget(preferred: HTMLElement | null): HTMLElement | null {
  if (preferred && isVisibleForFocus(preferred)) return preferred;
  if (!preferred?.parentElement) return null;

  let ancestor: HTMLElement | null = preferred.parentElement;
  while (ancestor) {
    const candidates = ancestor.querySelectorAll<HTMLElement>(CLOSE_FOCUS_FALLBACK_SELECTOR);
    for (const candidate of candidates) {
      if (candidate !== preferred && isVisibleForFocus(candidate)) {
        return candidate;
      }
    }
    ancestor = ancestor.parentElement;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/*  View — modal bottom sheet                                          */
/* ------------------------------------------------------------------ */

export interface MobileFilterDrawerViewProps {
  /** Whether the sheet is open. Closed by default so the fold belongs to results. */
  readonly open: boolean;
  /** Sheet open/close. Apply / Escape / scrim / drag-dismiss all call this. */
  readonly onOpenChange: (open: boolean) => void;
  /**
   * Live event count for the apply button. Pass `undefined` while the facets
   * query is loading; the button reads "Apply" until a number arrives.
   */
  readonly total: number | undefined;
  /** When true, the CLEAR ALL ghost is enabled. */
  readonly canClear: boolean;
  /** Wipes every discover filter (URL reset to {@link defaultDiscoverParams}). */
  readonly onClearAll: () => void;
  /**
   * Ref to the FILTERS trigger in the results bar. Required for focus return
   * — the sheet has no DialogTrigger of its own (NavMenuSheet / WEB-613 seam).
   */
  readonly triggerRef?: React.RefObject<HTMLElement | null>;
  /** Optional className applied to the sheet surface. */
  readonly className?: string;

  readonly activeFiltersSlot: React.ReactNode;
  /**
   * Sort control (WEB-668 / DPL-18; WEB-1074). The context bar shows Sort
   * inline only above 980px; at ≤980 — every width where this sheet is
   * reachable — Sort lives here, at the top of the sheet. Optional so the
   * View keeps rendering standalone in stories/tests that predate it; the
   * connected drawer always supplies it.
   */
  readonly sortSlot?: React.ReactNode;
  /**
   * Rail-head cards (WEB-937 / WEB-943) — taste card for a signed-in viewer
   * with signal, access card for anonymous. Optional so the View stays
   * standalone; the connected drawer supplies `<DiscoverRailHeadCards />`.
   * Both cards return null when they do not apply. Wrapper is `empty:hidden`.
   * Sits under Sort, above Date (v3 sheet order).
   */
  readonly tasteCardSlot?: React.ReactNode;
  readonly dateSlot: React.ReactNode;
  /**
   * Scope caption (WEB-659 / DPL-09) — mirrors the desktop rail's slot of
   * the same name. Optional so the View keeps rendering standalone in
   * stories/snapshots that predate it.
   */
  readonly scopeCaptionSlot?: React.ReactNode;
  readonly categorySlot: React.ReactNode;
  readonly neighborhoodSlot: React.ReactNode;
  readonly priceSlot: React.ReactNode;
  /**
   * "Only who I follow" toggle (WEB-662 / DPL-12). Optional so the View
   * keeps rendering standalone in stories/snapshots that predate it.
   */
  readonly followToggleSlot?: React.ReactNode;
}

interface DrawerSectionProps {
  readonly heading?: string;
  readonly testId: string;
  readonly children: React.ReactNode;
}

function DrawerSection({ heading, testId, children }: DrawerSectionProps): React.ReactElement {
  const headingId = heading ? `${testId}-heading` : undefined;
  return (
    <section data-testid={testId} className="flex flex-col gap-2" aria-labelledby={headingId}>
      {heading ? (
        <h3 id={headingId} className="px-1 text-sm font-semibold text-foreground">
          {heading}
        </h3>
      ) : null}
      <div>{children}</div>
    </section>
  );
}

/**
 * Drag-to-dismiss on the grabber header only (WEB-679). Internal sheet
 * scrolling is untouched — pointer handlers live on the grabber, not the body.
 */
function useGrabberDismiss(onDismiss: () => void): {
  dragY: number;
  isDragging: boolean;
  grabberProps: {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  };
} {
  const startYRef = React.useRef<number | null>(null);
  const [dragY, setDragY] = React.useState(0);

  const endDrag = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (startYRef.current === null) return;
      const delta = Math.max(0, event.clientY - startYRef.current);
      startYRef.current = null;
      setDragY(0);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      if (delta >= MOBILE_FILTER_SHEET_DRAG_DISMISS_PX) {
        onDismiss();
      }
    },
    [onDismiss],
  );

  const grabberProps = React.useMemo(
    () => ({
      onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
        if (event.button !== 0) return;
        startYRef.current = event.clientY;
        event.currentTarget.setPointerCapture(event.pointerId);
      },
      onPointerMove: (event: React.PointerEvent<HTMLElement>) => {
        if (startYRef.current === null) return;
        setDragY(Math.max(0, event.clientY - startYRef.current));
      },
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    }),
    [endDrag],
  );

  return { dragY, isDragging: startYRef.current !== null || dragY > 0, grabberProps };
}

/**
 * Mark the Discover results pane `inert` for the open lifetime so event
 * cards leave the tab order and the accessibility tree. The Sheet primitive
 * already converts Radix `hideOthers` markers via `useInertBackground`, but
 * that only covers portal *siblings*. In the live App Router tree the
 * results pane is not always under one of those siblings, so the AC
 * ("results region is inert") is owned here.
 */
function useResultsRegionInert(active: boolean): void {
  React.useEffect(() => {
    if (!active || typeof document === 'undefined') return;
    const marked = new Set<HTMLElement>();
    const apply = (): void => {
      const region = document.querySelector<HTMLElement>(
        `[data-testid="${DISCOVER_RESULTS_REGION_TEST_ID}"]`,
      );
      if (!region || region.hasAttribute('inert')) return;
      region.setAttribute('inert', '');
      marked.add(region);
    };
    apply();
    const observer = new MutationObserver(apply);
    const initial = document.querySelector<HTMLElement>(
      `[data-testid="${DISCOVER_RESULTS_REGION_TEST_ID}"]`,
    );
    observer.observe(initial?.parentElement ?? document.body, {
      childList: true,
      subtree: true,
    });
    return () => {
      observer.disconnect();
      marked.forEach((node) => node.removeAttribute('inert'));
    };
  }, [active]);
}

/**
 * Pure presentational shell for the mobile filter sheet (WEB-679 / DPL-28).
 * A real modal dialog — scrim, body scroll lock, `inert` results (this
 * View marks `discover-view-pane`; the Sheet primitive also converts Radix
 * `hideOthers` via `useInertBackground`), focus trap, Escape, drag-to-dismiss
 * on the grabber header, and a sticky action footer.
 *
 * The FILTERS trigger lives in `DiscoverResultsBar` (results-bar host owns
 * it, same seam as `NavMenuSheet` / WEB-613). Pass `triggerRef` so close
 * restores focus to that pill.
 */
export function MobileFilterDrawerView({
  open,
  onOpenChange,
  total,
  canClear,
  onClearAll,
  triggerRef,
  className,
  activeFiltersSlot,
  sortSlot,
  tasteCardSlot,
  dateSlot,
  scopeCaptionSlot,
  categorySlot,
  neighborhoodSlot,
  priceSlot,
  followToggleSlot,
}: MobileFilterDrawerViewProps): React.ReactElement {
  const handleClose = React.useCallback(() => onOpenChange(false), [onOpenChange]);
  const { dragY, isDragging, grabberProps } = useGrabberDismiss(handleClose);
  useResultsRegionInert(open);
  const applyLabel = formatApplyLabel(total);
  const openerRef = React.useRef<HTMLElement | null>(null);

  const handleOpenAutoFocus = React.useCallback(() => {
    openerRef.current = document.activeElement as HTMLElement | null;
  }, []);

  const handleCloseAutoFocus = React.useCallback(
    (event: Event) => {
      const preferred = triggerRef?.current ?? openerRef.current;
      openerRef.current = null;
      event.preventDefault();
      resolveCloseFocusTarget(preferred)?.focus();
    },
    [triggerRef],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        title={null}
        description="Sort and filter events by date, category, neighborhood, price, and more."
        overlayClassName={SHEET_OVERLAY_CLASS}
        showClose={false}
        aria-labelledby="mobile-filter-drawer-title"
        aria-modal="true"
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={handleCloseAutoFocus}
        data-testid="mobile-filter-drawer-sheet"
        style={
          {
            [MOBILE_FILTER_SHEET_DRAG_Y_VAR]: `${dragY}px`,
            ...(isDragging || dragY > 0 ? { transition: 'none' } : {}),
          } as React.CSSProperties
        }
        className={cn(SHEET_CONTENT_CLASS, className)}
      >
        <SheetTitle id="mobile-filter-drawer-title" className="sr-only">
          Filters
        </SheetTitle>

        <div
          data-testid="mobile-filter-drawer-header"
          className="flex shrink-0 flex-col border-b border-border"
        >
          <div
            data-testid="mobile-filter-drawer-grabber"
            className="flex w-full cursor-grab touch-none items-center justify-center py-2.5 active:cursor-grabbing"
            {...grabberProps}
          >
            <span aria-hidden="true" className="h-1 w-[38px] rounded-full bg-fg-3" />
          </div>
          <div className="flex items-center justify-between gap-3 px-4 pb-3">
            <p className="m-0 text-body font-semibold leading-none text-foreground">Filters</p>
            <SheetClose asChild>
              <button
                type="button"
                aria-label="Close filters"
                data-testid="mobile-filter-drawer-close"
                className={cn(
                  // WEB-1076 / FRE-1379: 34×34 paint; `.a-hit-floor` is the
                  // coarse-pointer 44×44 hit box.
                  'a-hit-floor inline-flex size-8.5 shrink-0 items-center justify-center rounded-full',
                  'text-foreground transition-colors hover:bg-muted',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring',
                )}
              >
                <XIcon aria-hidden="true" className="size-4" strokeWidth={2} />
              </button>
            </SheetClose>
          </div>
        </div>

        <div
          id={MOBILE_FILTER_SHEET_PANEL_ID}
          data-testid="mobile-filter-drawer-body"
          className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-4"
        >
          {sortSlot ? (
            <DrawerSection heading="Sort" testId="mobile-filter-drawer-sort">
              {sortSlot}
            </DrawerSection>
          ) : null}

          {tasteCardSlot ? (
            <div data-testid="mobile-filter-drawer-taste-card" className="empty:hidden">
              {tasteCardSlot}
            </div>
          ) : null}

          <div data-testid="mobile-filter-drawer-active-filters">{activeFiltersSlot}</div>

          <DrawerSection heading="Date" testId="mobile-filter-drawer-date">
            {dateSlot}
          </DrawerSection>

          <DrawerSection testId="mobile-filter-drawer-category">
            {scopeCaptionSlot ? (
              <div data-testid="mobile-filter-drawer-scope-caption">{scopeCaptionSlot}</div>
            ) : null}
            {categorySlot}
          </DrawerSection>

          <DrawerSection testId="mobile-filter-drawer-neighborhood">
            {neighborhoodSlot}
          </DrawerSection>

          <DrawerSection testId="mobile-filter-drawer-price">{priceSlot}</DrawerSection>

          {followToggleSlot ? (
            <div data-testid="mobile-filter-drawer-follow-toggle" className="empty:hidden">
              {followToggleSlot}
            </div>
          ) : null}
        </div>

        <div
          data-testid="mobile-filter-drawer-footer"
          className="flex shrink-0 gap-2 border-t border-border px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          <button
            type="button"
            data-testid="mobile-filter-drawer-clear"
            onClick={onClearAll}
            disabled={!canClear}
            className={cn(
              // WEB-1076: the sheet's primary actions paint at 44px on both
              // pointers; that is their visual weight, not a touch floor.
              'a-hit-floor inline-flex min-h-11 items-center justify-center rounded-md px-4',
              'font-mono text-micro font-semibold uppercase tracking-[0.04em]',
              'text-foreground hover:bg-muted disabled:pointer-events-none disabled:opacity-40',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring',
            )}
          >
            Clear all
          </button>
          <button
            type="button"
            data-testid="mobile-filter-drawer-apply"
            onClick={handleClose}
            className={cn(
              'a-hit-floor inline-flex min-h-11 flex-1 items-center justify-center rounded-md',
              'bg-clt-skyline px-4 text-sm font-semibold text-white',
              'transition-colors hover:bg-clt-skyline/90',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-skyline focus-visible:ring-offset-2',
            )}
          >
            {applyLabel}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ */
/*  Connected — wires View to URL state + facets count                 */
/* ------------------------------------------------------------------ */

export interface MobileFilterDrawerProps {
  /**
   * Active city — forwarded to the facets query and the connected filter
   * molecules so every request carries the geo anchor (WEB-263).
   */
  readonly cityCode: CityCode;
  /**
   * SSR envelope total forwarded to the anonymous access card (WEB-937)
   * so the sheet `{N}` matches the results bar on first paint (WEB-786).
   */
  readonly initialTotal?: number;
  /** Serialized SSR discover-args scope for the access-card `{N}` fallback. */
  readonly initialTotalsScope?: string;
  /** Wrapper class names forwarded to the View. */
  readonly className?: string;
  /**
   * Uncontrolled initial open. Defaults to `false`. Useful for stories or
   * tests that want to deep-link directly into an open sheet.
   */
  readonly defaultOpen?: boolean;
  /** Controlled open — the results-bar host owns this (WEB-679). */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  /** Results-bar FILTERS pill — focus returns here on close. */
  readonly triggerRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Connected wrapper for the mobile filter sheet (WEB-679 / DPL-28). Composes
 * the Discover filter molecules — sort, rail-head cards, date, category,
 * neighborhood, price and follow — into a
 * dismissible modal bottom sheet. URL state is the single source of truth —
 * filter changes apply live,
 * CLEAR ALL resets the URL, and Show N events just closes the sheet.
 *
 * Focus management is owned by the Sheet primitive (Radix dialog): Tab is
 * trapped while open and returned to `triggerRef` on close.
 *
 * Visibility: the host trigger uses `min-[981px]:hidden`. This sheet closes
 * itself when the viewport crosses that breakpoint so a desktop resize never
 * leaves a mobile modal stranded over the rail.
 */
export function MobileFilterDrawer({
  cityCode,
  initialTotal,
  initialTotalsScope,
  className,
  defaultOpen = false,
  open: openProp,
  onOpenChange: onOpenChangeProp,
  triggerRef,
}: MobileFilterDrawerProps): React.ReactElement {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChangeProp?.(next);
    },
    [isControlled, onOpenChangeProp],
  );

  const { params, replaceParams, setParams } = useDiscoverParams();

  const activeFilterCount = React.useMemo(() => countActiveFilters(params), [params]);

  // WEB-791 / DPL-16 — the sort menu is viewer-invariant: the same four
  // options, with the same labels, in the sheet and in the results bar.
  const sortOptions = React.useMemo(() => buildSortOptions(), []);
  const handleSortChange = React.useCallback(
    (next: SortMode) => setParams({ sort: next }),
    [setParams],
  );

  const handleClearAll = React.useCallback(() => {
    replaceParams(defaultDiscoverParams());
  }, [replaceParams]);

  // Close when the desktop rail takes over (resize past 981).
  React.useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const media = window.matchMedia(`(min-width: ${MOBILE_FILTER_SHEET_BREAKPOINT_PX}px)`);
    const onChange = (): void => {
      if (media.matches) setOpen(false);
    };
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [open, setOpen]);

  // WEB-730: this subscription MUST key identically to the list view's, or RTK
  // Query opens a second cache entry and fires a duplicate `/events/discover`
  // request. Funnelling through the shared hook makes that impossible.
  const facetsArgs = useDiscoverEventArgs(params, cityCode);
  // WEB-1096: fetch errors (toast + 4xx revert) belong to the template's
  // `DiscoverErrorHost`, which sees the drawer's params changes like any other.
  const { currentData } = useDiscoverFacetsQuery(facetsArgs);

  return (
    <MobileFilterDrawerView
      className={className}
      open={open}
      onOpenChange={setOpen}
      total={currentData?.meta.total}
      canClear={activeFilterCount > 0}
      onClearAll={handleClearAll}
      triggerRef={triggerRef}
      activeFiltersSlot={<DiscoverActiveFilters cityCode={cityCode} />}
      sortSlot={
        <SelectMenu
          value={params.sort}
          onValueChange={(value) => handleSortChange(value as SortMode)}
        >
          {/* WEB-1020 ruling: DS `Button` `ghost` md, full width in the sheet. */}
          <SelectMenuTrigger
            data-testid="mobile-filter-drawer-sort-select"
            aria-label="Sort events by"
            className="w-full justify-between font-mono text-micro uppercase tracking-[0.04em]"
          >
            <SelectMenuValue placeholder="Sort by" />
          </SelectMenuTrigger>
          {/* Portals to body at the primitive's z-50; ride `--z-modal-nested`
              so the menu paints above this sheet's `--z-modal` overlay. */}
          <SelectMenuContent className="z-modal-nested">
            {sortOptions.map((option) => (
              <SelectMenuItem key={option.key} value={option.key}>
                {option.label}
              </SelectMenuItem>
            ))}
          </SelectMenuContent>
        </SelectMenu>
      }
      tasteCardSlot={
        <DiscoverRailHeadCards
          cityCode={cityCode}
          initialTotal={initialTotal}
          initialTotalsScope={initialTotalsScope}
        />
      }
      dateSlot={<DiscoverDatePills />}
      scopeCaptionSlot={
        <DiscoverScopeCaptionView
          categoryCount={CATEGORY_ORDER.length}
          eventCount={currentData?.meta?.total ?? null}
          dateLabel={formatDiscoverScopeCaptionPhrase(
            resolveDiscoverScopeKey(currentData?.facets?.scopeKey, params.date),
          )}
        />
      }
      categorySlot={<DiscoverCategoryFilter cityCode={cityCode} />}
      neighborhoodSlot={<DiscoverNeighborhoodFilter cityCode={cityCode} />}
      priceSlot={
        <DiscoverPricePresets cityCode={cityCode} id="mobile-filter-drawer-price-presets" />
      }
      followToggleSlot={
        <DiscoverFollowToggle cityCode={cityCode} id="mobile-filter-drawer-follow-toggle-switch" />
      }
    />
  );
}
